import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Box,
  Chip,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../utils/apiConfig';

const TenantMultiSelect = ({ 
  value = '', // Komma-getrennte String (z.B. "1,2,3")
  onChange, 
  label = 'Tenants',
  helperText = '',
  disabled = false,
  required = false 
}) => {
  const { t } = useTranslation();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);

  // Konvertiere komma-getrennten String zu Array
  const selectedTenantIds = value ? value.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id)) : [];

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const response = await apiFetch('/tenants/');
      if (response.ok) {
        const data = await response.json();
        setTenants(data);
      } else {
        console.error('Failed to fetch tenants:', response.status);
      }
    } catch (error) {
      console.error('Error fetching tenants:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event) => {
    const selectedIds = event.target.value;
    
    // Konvertiere Array zurück zu komma-getrenntem String
    const tenantString = selectedIds.length > 0 ? selectedIds.join(',') : '';
    
    if (onChange) {
      onChange(tenantString);
    }
  };

  const getSelectedTenantNames = () => {
    return selectedTenantIds
      .map(id => tenants.find(tenant => tenant.id === id))
      .filter(tenant => tenant)
      .map(tenant => tenant.bezeichnung);
  };

  const renderValue = (selected) => {
    if (selected.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t('tenantSelect.allTenants', 'Alle Tenants')}
        </Typography>
      );
    }

    const selectedNames = getSelectedTenantNames();
    
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {selectedNames.map((name) => (
          <Chip key={name} label={name} size="small" />
        ))}
      </Box>
    );
  };

  return (
    <FormControl fullWidth disabled={disabled || loading} required={required}>
      <InputLabel id="tenant-multiselect-label">{label}</InputLabel>
      <Select
        labelId="tenant-multiselect-label"
        multiple
        value={selectedTenantIds}
        onChange={handleChange}
        input={<OutlinedInput label={label} />}
        renderValue={renderValue}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 224,
              width: 250,
            },
          },
        }}
      >
        {/* "Alle Tenants" Option */}
        <MenuItem value={[]}>
          <Checkbox checked={selectedTenantIds.length === 0} />
          <ListItemText 
            primary={t('tenantSelect.allTenants', 'Alle Tenants')} 
            secondary={t('tenantSelect.allTenantsHelp', 'Für alle Mandanten verfügbar')}
          />
        </MenuItem>

        {/* Einzelne Tenants */}
        {tenants.map((tenant) => (
          <MenuItem key={tenant.id} value={tenant.id}>
            <Checkbox checked={selectedTenantIds.includes(tenant.id)} />
            <ListItemText 
              primary={tenant.bezeichnung}
              secondary={tenant.hl7_name ? `HL7: ${tenant.hl7_name}` : ''}
            />
          </MenuItem>
        ))}
      </Select>
      
      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
          {helperText}
        </Typography>
      )}
    </FormControl>
  );
};

export default TenantMultiSelect;
